/**
 * AFRIQAHOME: types.ts
 * 
 * UPDATES:
 * - Added serviceCharge?: number (for monthly cost calculator)
 * - Added nextOpenHouse?: string (ISO date for open house viewings)
 */

export enum PropertyType {
  VILLA = 'Villa',
  APARTMENT = 'Apartment',
  TOWNHOUSE = 'Townhouse',
  BUNGALOW = 'Bungalow',
  PLOT = 'Plot',
  PROJECT = 'New Development' // New type for parent listings
}

export enum ListingStatus {
  AVAILABLE = 'AVAILABLE',
  SOLD = 'SOLD',
  PENDING = 'PENDING',
  RESERVED = 'RESERVED'
}

export type AgentTier = 'Ignite' | 'Elevate' | 'Dominate';
export type VerificationStatus = 'Pending' | 'Verified' | 'Rejected';
export type AgentStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED';

export type UserRole = 'buyer' | 'agent' | 'admin';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
}

export interface AgentProfile {
  id: string;
  name: string;
  agencyName?: string;
  image: string;
  phone: string;
  tier: AgentTier;
  email: string;
  
  // Status & Verification
  status: AgentStatus;       // Admin controlled status
  wantsVerification?: boolean;
  isVerified?: boolean;      // Result of admin approval
  verificationDocuments?: {
    idUploaded: boolean;
    licenseUploaded: boolean;
  };

  // Timestamps for sorting
  createdAt?: number;
  updatedAt?: number;
}

export interface TeamMember {
  id: string;
  name: string;
  image: string;
  activeListings: number;
  totalSales: number; // In KES
  leads: number;
}

export interface PropertyUnit {
  id: string;
  unitNumber: string; // Acts as "Unit Type/Name" e.g. "2 Bed Type A"
  price: number;
  bedrooms: number;
  bathrooms: number;
  size: number;
  status: 'Available' | 'Sold' | 'Reserved';
  floor: string; // Floor or floor range, e.g. "1", "1-12", "Ground-12"
  units_available: number;
}

export interface Property {
  id: string;
  slug?: string;  // URL-safe slug for SEO-friendly URLs
  address: string;
  city: string;
  neighborhood?: string;
  price: number;
  maxPrice?: number; // For Parent Listings (Price Range)
  currency: string;
  rooms: number; // Bedrooms
  roomsMax?: number | null; // For New Developments: max bedrooms in range
  bathrooms: number;
  bathroomsMax?: number | null; // For New Developments: max bathrooms in range
  area: number; // in sqm
  type: PropertyType;
  status?: ListingStatus; 
  image: string; // Main image (thumbnail)
  images: string[]; // Gallery images
  description: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  features: string[]; // Strings for display
  
  // =============================================
  // NEW: Hemnet/Booli-style fields
  // =============================================
  serviceCharge?: number; // Monthly service/maintenance fee (KES)
  nextOpenHouse?: string; // ISO date string for scheduled viewing, e.g. "2025-02-15T14:00:00"
  
  // Kenyan Market Infrastructure (Must-Haves)
  titleReady: boolean;
  borehole: boolean;
  backupGenerator: boolean;
  nearTarmac: boolean;
  staffQuarters: boolean; // DSQ

  // Lifestyle Amenities
  airCon: boolean;
  balcony: boolean;
  furnished: boolean;
  garage: boolean;
  garden: boolean;
  pool: boolean;
  gym: boolean;
  fireplace: boolean;
  security: boolean; // CCTV/Electric Fence

  // Commercial Logic
  isSpotlight?: boolean; // Revenue stream: Boosted listing

  // --- NEW CONSTRUCTION / PROJECTS ---
  isParentListing?: boolean; // Is this the main "Project" page?
  developmentId?: string; // Links units to the parent listing
  unitNumber?: string; // e.g., "A4", "12B"
  floor?: number; // e.g., 5
  
  // The list of units inside this project
  units?: PropertyUnit[];

  agent: {
  name: string;
  image: string;
  phone: string;
  tier?: 'ignite' | 'elevate' | 'dominate';
  agency_name?: string;
  agency_logo_url?: string;
  };
}

export interface FilterState {
  minPrice: number;
  maxPrice: number;
  minArea: number;
  rooms: number | null;
  type: PropertyType | 'All';
  searchQuery: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  isThinking?: boolean;
}

// --- Direct Messaging Types ---

export interface Message {
  id: string;
  senderId: string;
  text: string;
  timestamp: number;
  isRead: boolean;
}

export interface Conversation {
  id: string;
  participants: User[]; // Includes both Buyer and Agent
  propertyId?: string; // Optional: If chat is about a specific property
  messages: Message[];
  lastMessageAt: number;
  unreadCount: number; // For the current user
}

// --- Financial & Marketing Types ---

export type TransactionType = 'SUBSCRIPTION' | 'VERIFICATION_FEE' | 'SPOTLIGHT_BOOST';

export interface Transaction {
  id: string;
  agentName: string;
  agentId: string;
  type: TransactionType;
  amount: number;
  date: number;
  status: 'COMPLETED' | 'PENDING';
}

export type CampaignTarget = 'IGNITE_USERS' | 'UNVERIFIED_AGENTS' | 'ALL_AGENTS';

export interface Campaign {
 id: string;
  target: CampaignTarget;
  title: string;
  body: string;
  ctaLabel: string;
  ctaUrl?: string | null; 
  isActive: boolean;
  createdAt: number;
}

// =============================================
// HELPER FUNCTIONS
// =============================================

/**
 * Format Open House date for display
 * Returns null if date is in the past
 */
export function formatOpenHouseDate(isoDate: string | undefined): string | null {
  if (!isoDate) return null;
  
  const date = new Date(isoDate);
  const now = new Date();
  
  // Return null if date is in the past
  if (date < now) return null;
  
  // Format: "Sat 15 Feb, 14:00"
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  };
  
  return date.toLocaleDateString('en-GB', options).replace(',', '');
}

/**
 * Check if open house is within next 7 days (for highlighting)
 */
export function isOpenHouseSoon(isoDate: string | undefined): boolean {
  if (!isoDate) return false;
  
  const date = new Date(isoDate);
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  return date > now && date <= sevenDaysFromNow;
}

/**
 * Calculate monthly mortgage payment using annuity formula
 * 
 * @param principal - Loan amount (price - down payment)
 * @param annualRate - Annual interest rate (e.g., 0.135 for 13.5%)
 * @param years - Loan term in years
 * @returns Monthly payment amount
 */
export function calculateMonthlyPayment(
  principal: number,
  annualRate: number,
  years: number
): number {
  // Monthly interest rate
  const r = annualRate / 12;
  // Total number of payments
  const n = years * 12;
  
  // Annuity formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
  if (r === 0) {
    // If no interest, simple division
    return principal / n;
  }
  
  const payment = principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  
  return Math.round(payment);
}